import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { UserFormAddItemService } from '../../../core/services/user-form-add-item.service';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { FileService } from '../../../core/services/file.service';
import { BrowserCheckService } from '../../../core/services/browser-check.service';
import { constantTxt } from '../../../core/constants/constants';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { Subscription } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal'; 
import { NzModalModule } from 'ng-zorro-antd/modal'; 
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-user-form-add-item',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzSelectModule,
    NzInputModule,
    NzIconModule,
    NzUploadModule,
    NzModalModule,
  ],
  templateUrl: './user-form-add-item.component.html',
  styleUrls: ['./user-form-add-item.component.less'],
})
export class UserFormAddItemComponent implements OnInit, OnDestroy {
  isViewMode = false;
  attachments: any[] = [];
  itemForm!: FormGroup;
  uoms: any[] = [];
  priority: any[] = [];
  saving = false;
  attach1?: File;
  attach2?: File;
  attach3?: File;
  successMessage: string = '';
  itemDetails: any;
  isEditMode = false;
  pnaNoTemp: number = 0;
  pnaYearTemp: string = '';
  itemNoTemp: number = 0;
  attachmentTempDto: [] = [];
  selectedQACodes: string = '';
  pnaIdTemp: string = '';
  private messageListener?: (event: MessageEvent) => void;
  constants = constantTxt;
  returnUrl: string = '';
  returnMode: string = '';

  fileName: string | undefined;
  allowedFileExt = (environment.allowedFileFormats || '').split(',').map(e => e.trim().toLowerCase()).filter(Boolean); allowedZipInnerExt = (environment.allowedZipInnerFileFormats || '').split(',').map(e => e.trim().toLowerCase()).filter(Boolean);

  getUomSubscription!: Subscription;
  getPrioritySubscription!: Subscription;
  updateItemSubscription!: Subscription;
  saveItemSubscription!: Subscription;
  deleteAttachmentSubscription!: Subscription;
  getItemDetailsSubscription!: Subscription;
  getAttachmentsSubscription!: Subscription;
  downloadFileSubscription!: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly fileService: FileService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly userFormAddItemService: UserFormAddItemService,
    private readonly location: Location,
    private readonly browserCheck: BrowserCheckService,
    private readonly modal: NzModalService
  ) {
    this.fileName = '';
  }

  ngOnInit() {
    this.initializeForm();

    // Sync initial form value with component property
    this.itemNoTemp = this.itemForm.get('itemTempDto.itemNoTemp')?.value || 0;

    // Use the finalized values for setItemNo if we're in add mode
    this.initializeState();
    this.loadApiData();
    this.subscribeWatchers();
  }

  initializeForm(): void {
    this.itemForm = this.fb.group({
      itemTempDto: this.fb.group({
        itemId: [0],
        itemPnaNoTemp: [0],
        itemPnaYearTemp: [''],
        itemNoTemp: [0],
        itemManufacturer: [''],
        itemDesc: ['', Validators.required],
        itemQuantity: [
          '',
          [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/)],
        ],
        itemUom: ['', Validators.required],
        itemPriority: [''],
        itemJdeItemNo: [''],
        itemQa: [''],
        itemSuggestSupplier: [''],
        itemComment: [''],
      }),
      attachmentTempDto: this.fb.group({
        attachId: [0],
        attachPnaNoTemp: [0],
        attachPnaYearTemp: [''],
        attachItemNoTemp: [0],
        attachPath: [''],
        fileName: [''],
      }),
    });
  }

  initializeState() {
    const navigationState =
      this.router.getCurrentNavigation()?.extras?.state || history.state;
    this.pnaIdTemp = navigationState?.pnaIdTemp || '';
    const pnaNo = navigationState?.pnaNo;
    const yearFromNav = navigationState?.year;
    this.returnUrl = navigationState?.returnUrl;
    const itemId = navigationState?.itemId;
    const routePath = this.route.snapshot.url[0]?.path || '';
    const modeParam =
      navigationState?.mode || this.route.snapshot.queryParamMap.get('mode');
    this.returnMode = navigationState?.returnMode;

    let finalPnaNo = null;
    let finalYear = null;

    if (this.pnaIdTemp) {
      finalPnaNo = Number(this.pnaIdTemp);
      finalYear = new Date().getFullYear().toString();
    } else if (pnaNo) {
      this.pnaIdTemp = pnaNo;
      finalPnaNo = Number(pnaNo);
      finalYear = yearFromNav || new Date().getFullYear().toString();
    }

    // Patch the form if we have valid PNA data
    if (finalPnaNo && finalPnaNo > 0) {
      this.itemForm.patchValue({
        itemTempDto: {
          itemPnaNoTemp: finalPnaNo,
          itemPnaYearTemp: finalYear,
        },
        attachmentTempDto: {
          attachPnaNoTemp: finalPnaNo,
          attachPnaYearTemp: finalYear,
        },
      });
    }

    if (itemId) {
      // This is edit or view mode - load existing item details
      if (routePath === 'view-item' || modeParam === 'view') {
        this.setViewMode();
      } else if (routePath === 'edit-item') {
        this.setEditMode();
      }
      this.loadItemDetails(+itemId);
    } else {
      // This is add mode - generate new item number
      this.setItemNo();
    }
  }

  loadApiData(): void {
    this.loadUomData();
    this.loadPriorityData();
    this.setAlert();
    this.loadSelectedQACodes();
  }

  loadUomData(): void {
    this.getUomSubscription = this.userFormAddItemService.getUom().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.uoms = data;
        }
      },
      error: (error) => {
        console.error('Error fetching UOM data:', error);
      },
    });
  }

  loadPriorityData(): void {
    this.getPrioritySubscription = this.userFormAddItemService
      .getPriority()
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.priority = data.map((p) => ({
              priorityName: p.priorityName,
              priorityNameWithValue: `${p.priorityNo}. ${p.priorityName}`,
            }));
          }
        },
        error: (error) => {
          console.error('Error fetching Priority data:', error);
        },
      });
  }

  subscribeWatchers(): void {
    this.itemForm.get('itemTempDto.itemQa')?.valueChanges.subscribe((value) => {
      this.selectedQACodes = value || '';
    });

    // Sync the component property with the form control
    this.itemForm
      .get('itemTempDto.itemNoTemp')
      ?.valueChanges.subscribe((value) => {
        if (value !== undefined && value !== null) {
          this.itemNoTemp = value;
        }
      });
  }

  setItemNo(): void {
    const existingItemCount = this.getExistingItemCountFromParams();
    this.itemNoTemp = existingItemCount + 1;

    this.itemForm.patchValue({
      itemTempDto: { itemNoTemp: this.itemNoTemp },
    });
  }

  private getExistingItemCountFromParams(): number {
    // Try to get data from router navigation state (cleanest approach)
    const navigationState =
      this.router.getCurrentNavigation()?.extras?.state || history.state;

    if (navigationState?.itemCount !== undefined) {
      return parseInt(navigationState.itemCount, 10);
    }

    // Second fallback: Try to get it from local storage for this PNA
    const pnaNo = navigationState?.pnaNo || this.pnaIdTemp;
    const storageKey = `itemCount_${pnaNo}`;
    const storedCount = localStorage.getItem(storageKey);

    if (storedCount) {
      return parseInt(storedCount, 10);
    }

    // Default to 0 if no existing count found
    return 0;
  }

  private updateItemCountInStorage(): void {
    const navigationState = history.state;
    const pnaNo = navigationState?.pnaNo || this.pnaIdTemp;
    const storageKey = `itemCount_${pnaNo}`;

    if (this.itemNoTemp) {
      localStorage.setItem(storageKey, this.itemNoTemp.toString());
    }
  }

  checkValidation(): boolean {
    const itemDesc = this.itemForm.get('itemTempDto.itemDesc')?.value;
    if (!itemDesc || itemDesc.trim() === '') {
      alert('Please input about description.');
      const descElement = document.querySelector(
        'input[formControlName="itemDesc"], textarea[formControlName="itemDesc"]'
      ) as HTMLElement;
      if (descElement) descElement.focus();
      return false;
    }

    const itemQuantity = this.itemForm.get('itemTempDto.itemQuantity')?.value;
    if (!itemQuantity || itemQuantity.toString().trim() === '') {
      alert('Please input about Quantity.');
      const qtyElement = document.querySelector(
        'input[formControlName="itemQuantity"]'
      ) as HTMLElement;
      if (qtyElement) qtyElement.focus();
      return false;
    } else if (!this.ValidNumber(itemQuantity)) {
      alert('Quantity is only number.');
      const qtyElement = document.querySelector(
        'input[formControlName="itemQuantity"]'
      ) as HTMLElement;
      if (qtyElement) qtyElement.focus();
      return false;
    }

    const itemUom = this.itemForm.get('itemTempDto.itemUom')?.value;
    if (!itemUom || itemUom.toString().trim() === '') {
      alert('Please choose UOM.');
      const uomElement = document.querySelector(
        'select[formControlName="itemUom"]'
      ) as HTMLElement;
      if (uomElement) uomElement.focus();
      return false;
    }

    return true;
  }

  ValidNumber(value: any): boolean {
    const numberPattern = /^\d+(\.\d+)?$/;
    return numberPattern.test(value.toString());
  }

  onFileChange(event: any, fileNumber: number): void {
    const file: File = event.target.files?.[0];
    if (!file) return;
    if (!this.validateSelectedFile(file)) {
      event.target.value = '';
      return;
    }
    if (fileNumber === 1) this.attach1 = file;
    if (fileNumber === 2) this.attach2 = file;
    if (fileNumber === 3) this.attach3 = file;
  }
  private getExt(name: string): string {
    const i = name.lastIndexOf('.');
    return i >= 0 ? name.substring(i).toLowerCase() : '';
  }
  private showErrorModal(message: string): void {
    this.modal.error({ nzTitle: 'File Validation Error', nzContent: message.replace(/\n/g, '<br/>') });
  }
  private validateSelectedFile(file: File): boolean {
    const ext = this.getExt(file.name);
    if (!this.allowedFileExt.includes(ext)) {
      this.showErrorModal(`File type ${ext || '(none)'} is not allowed. Allowed types: ${this.allowedFileExt.join(', ')}`);
      return false;
    }
    return true;
  }
  private handleZipValidationError(err: any): void {
    const backendMsg = err?.error?.message || err?.error;
    const msg = `ZIP contains invalid files. Allowed inner file types: ${this.allowedZipInnerExt.join(', ')}${backendMsg ? '<br/><br/>' + backendMsg : ''}`;
    this.showErrorModal(msg);
  }
  onSave(): void {
  if (!this.checkValidation()) {
    return;
  }

  if (this.isEditMode) {
    if (!confirm('Are you sure to save this item?')) {
      return;
    }
  } else {
    if (!confirm('Are you sure to add this item?')) {
      return;
    }
  }

  const formData = new FormData();
  const itemTempDto = this.itemForm.value.itemTempDto;
  Object.keys(itemTempDto).forEach((key) => {
    formData.append(`ItemTempDto.${key}`, itemTempDto[key]);
  });
  const attachmentTempDto = this.itemForm.value.attachmentTempDto;
  Object.keys(attachmentTempDto).forEach((key) => {
    formData.append(`AttachmentTempDto.${key}`, attachmentTempDto[key]);
  });

  if (this.attach1) formData.append('Attach1', this.attach1);
  if (this.attach2) formData.append('Attach2', this.attach2);
  if (this.attach3) formData.append('Attach3', this.attach3);

  if (this.isEditMode) {
    this.updateItemSubscription = this.userFormAddItemService
      .updateItem(this.itemForm.get('itemTempDto.itemId')?.value, formData)
      .subscribe({
        next: () => {
          this.navigateBackToAddRequest();
        },
        error: (err: any) => {
          if (err.status === 413) {
            this.showErrorModal('The file you are trying to upload is too large. Please select a smaller file and try again.');
          } else if (err.status === 400) {
            this.handleZipValidationError(err);
          } else {
            this.showErrorModal('Error updating item.');
          }
          console.error('Error updating item:', err);
        },
      });
  } else {
    const currentFormValues = this.itemForm.value;
    let pnaNoToUse = currentFormValues.itemTempDto.itemPnaNoTemp;
    let yearToUse = currentFormValues.itemTempDto.itemPnaYearTemp;

    if (this.pnaIdTemp) {
      pnaNoToUse = Number(this.pnaIdTemp);
      yearToUse = new Date().getFullYear().toString();
    }

    this.itemForm.patchValue({
      itemTempDto: {
        itemPnaNoTemp: pnaNoToUse,
        itemPnaYearTemp: yearToUse,
      },
      attachmentTempDto: {
        attachPnaNoTemp: pnaNoToUse,
        attachPnaYearTemp: yearToUse,
      },
    });

    const finalItemTempDto = this.itemForm.value.itemTempDto;
    const finalAttachmentTempDto = this.itemForm.value.attachmentTempDto;

    const keysToRemove: string[] = [];
    formData.forEach((_value, key) => {
      if (key.startsWith('ItemTempDto.') || key.startsWith('AttachmentTempDto.')) {
        keysToRemove.push(key);
      }
    });
    keysToRemove.forEach((key) => formData.delete(key));

    Object.keys(finalItemTempDto).forEach((key) => {
      formData.append(
        `ItemTempDto.${key}`,
        finalItemTempDto[key] !== undefined && finalItemTempDto[key] !== null
          ? String(finalItemTempDto[key])
          : ''
      );
    });
    Object.keys(finalAttachmentTempDto).forEach((key) => {
      formData.append(
        `AttachmentTempDto.${key}`,
        finalAttachmentTempDto[key] !== undefined && finalAttachmentTempDto[key] !== null
          ? String(finalAttachmentTempDto[key])
          : ''
      );
    });

    const dataType = this.returnMode == 'add' ? 'temp' : 'data';
    this.saveItemSubscription = this.userFormAddItemService
      .saveItem(formData, dataType)
      .subscribe({
        next: () => {
          this.updateItemCountInStorage();
          this.navigateBackToAddRequest();
        },
        error: (err: any) => {
          if (err.status === 413) {
            this.showErrorModal('The file you are trying to upload is too large. Please select a smaller file and try again.');
          } else if (err.status === 400) {
            this.handleZipValidationError(err);
          } else {
            this.showErrorModal('Error saving item.');
          }
            console.error('Error saving item:', err);
          }
        });
    }
  }
  
  goSelectQA(): void {
    const currentQaValue =
      this.itemForm.get('itemTempDto.itemQa')?.value ||
      this.selectedQACodes ||
      '';
    this.messageListener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
  
      if (event.data && event.data.type === 'QA_SELECTION') {
        const selectedQACodes = event.data.selectedQACodes;
  
        if (selectedQACodes) {
          this.itemForm.get('itemTempDto.itemQa')?.setValue(selectedQACodes);
          this.selectedQACodes = selectedQACodes;
          this.cdr.detectChanges();
        }
  
        if (this.messageListener) {
          window.removeEventListener('message', this.messageListener);
          this.messageListener = undefined;
        }
      }
    };
  
    window.addEventListener('message', this.messageListener);
    const qaSelectionUrl = `/user-select-qa${
      currentQaValue ? '?preSelected=' + encodeURIComponent(currentQaValue) : ''
    }`;
  
    const qaWindow = window.open(
      qaSelectionUrl,
      'search',
      'width=720, height=600, scrollbars=1'
    );
  
    if (!qaWindow) {
      if (this.messageListener) {
        window.removeEventListener('message', this.messageListener);
        this.messageListener = undefined;
      }
      alert('Popup blocked. Please allow popups for this site.');
      return;
    }
  
    setTimeout(() => {
      if (this.messageListener && (qaWindow.closed || !qaWindow.window)) {
        window.removeEventListener('message', this.messageListener);
        this.messageListener = undefined;
      }
    }, 300000);
  }

  loadSelectedQACodes(): void {
    this.selectedQACodes = this.itemForm.get('itemTempDto.itemQa')?.value || '';
  }

  private navigateBackToAddRequest(): void {
    // Get navigation data from router state (preferred) or query parameters (fallback)
    const navigationState = history.state;
    const pnaNo = navigationState?.pnaNo;
    const year = navigationState?.year;
    const returnMode = navigationState?.returnMode;
    const pnaIdTemp = navigationState?.pnaIdTemp || this.pnaIdTemp;

    // Prepare return navigation state with all necessary data
    const returnNavigationState = {
      pnaIdTemp: pnaIdTemp,
      fromAddItem: true,
      mode: returnMode, // Pass the return mode through navigation state
      pnaNo: pnaNo, // Include PNA number for edit/view cases
      year: year, // Include year for edit/view cases
      returnUrl: '/user/request',
      role: navigationState?.role,
    };

    this.router.navigate([this.returnUrl], {
      state: returnNavigationState,
    });
  }

  goCancel(): void {
    const message = this.isEditMode
      ? 'Are you sure to cancel update this item?'
      : 'Are you sure to cancel add this item?';

    if (confirm(message)) {
      this.itemForm.reset();

      if (this.isEditMode) {
        this.location.back();
      } else {
        this.navigateBackToAddRequest();
      }
    }
  }

  setAlert(): void {
    if (this.browserCheck.isBrowser()) {
      const win = this.browserCheck.getWindow();
      if (win) {
        win.onbeforeunload = () => {
          if (!this.saving) {
            const answer = confirm(
              'Would you like to add item before leaving the page?'
            );
            if (answer === true) {
              if (!this.checkValidation()) {
                return;
              }

              if (confirm('Are you sure to add this item?')) {
                this.saving = true;
                this.itemForm.get('DoAction')?.setValue('ADD');
                this.onSave();
              }
            }
          }
        };
      }
    }
  }

  // loads item details based on itemId
  loadItemDetails(itemId: number): void {
    this.getItemDetailsSubscription = this.userFormAddItemService
      .getItemDetails(itemId)
      .subscribe(
        (response: any) => {
          const itemDetailsData = response.body;
          this.itemDetails =
            typeof itemDetailsData === 'string'
              ? JSON.parse(itemDetailsData)
              : itemDetailsData;
          const dataType = this.returnMode == 'add' ? 'temp' : 'data';
          this.fetchAttachments(
            dataType,
            this.itemDetails.itemPnaNoTemp,
            this.itemDetails.itemPnaYearTemp,
            this.itemDetails.itemNoTemp
          );
          this.patchFormValues();
        },
        (error) => {
          console.error('Error loading item details:', error);
        }
      );
  }

  patchFormValues(): void {
    if (this.itemDetails) {
      const itemData = {
        itemTempDto: {
          itemId: this.itemDetails.itemId,
          itemPnaNoTemp: this.itemDetails.itemPnaNoTemp,
          itemPnaYearTemp: this.itemDetails.itemPnaYearTemp,
          itemNoTemp: this.itemDetails.itemNoTemp,
          itemManufacturer: this.itemDetails.itemManufacturer,
          itemDesc: this.itemDetails.itemDesc,
          itemQuantity: this.itemDetails.itemQuantity,
          itemUom: this.itemDetails.itemUom,
          itemPriority: this.itemDetails.itemPriority,
          itemJdeItemNo: this.itemDetails.itemJdeItemNo,
          itemQa: this.itemDetails.itemQa,
          itemSuggestSupplier: this.itemDetails.itemSuggestSupplier,
          itemComment: this.itemDetails.itemComment,
        },
      };

      this.itemForm.patchValue(itemData);
      this.itemNoTemp = this.itemDetails.itemNoTemp;

      if (this.itemDetails.itemQa) {
        this.selectedQACodes = this.itemDetails.itemQa;
      } else {
        this.selectedQACodes = '';
      }
    }
  }

  goDelete(attachId: number, isTemp: boolean) {
    const confirmed = window.confirm(
      'Are you sure you want to delete this attachment?'
    );

    if (!confirmed) {
      return;
    }

    this.deleteAttachmentSubscription = this.userFormAddItemService
      .deleteAttachment(attachId, isTemp)
      .subscribe({
        next: () => {
          // Remove from local array regardless of response format
          this.attachments = this.attachments.filter(
            (attachment) => attachment.attachId !== attachId
          );
          alert('Attachment deleted successfully!');
        },
        error: (error: any) => {
          alert(
            'Failed to delete attachment. Error: ' +
              (error?.message || 'Unknown error')
          );
        },
      });
  }

  fetchAttachments(
    tableData: string,
    pnaNo: string,
    pnaYear: number,
    itemNo: string
  ) {
    this.getAttachmentsSubscription = this.userFormAddItemService
      .getAttachments(tableData, pnaNo, pnaYear, itemNo)
      .subscribe(
        (response: any) => {
          // Extract the body from the HTTP response
          const data = response.body || [];
          this.attachments = data.map((attach: any) => {
            const fileNameParts = attach.attachPath.split(/[/\\]/);
            let fileName = fileNameParts.pop();
            return {
              title: attach.title || 'Attachment',
              name: fileName,
              viewLink: attach.viewLink,
              attachId: attach.attachId,
              attachPath: attach.attachPath,
            };
          });
        },
        (error) => {
          console.error('Error fetching attachments:', error);
        }
      );
  }

  setViewMode(): void {
    this.isEditMode = false;
    this.isViewMode = true;
    this.itemForm.disable();
  }

  setEditMode(): void {
    this.isEditMode = true;
    this.isViewMode = false;
    this.itemForm.enable();
  }

  goBack(): void {
    if (this.isViewMode) {
      this.location.back();
      return;
    }

    if (confirm('Are you sure to cancel add this item?')) {
      this.itemForm.reset();
      this.location.back();
    }
  }

  downloadManual(path: string): void {
    this.downloadFileSubscription = this.fileService
      .downloadFile(path)
      .subscribe({
        next: (response: any) => {
          if (response.body) {
            const contentDisposition = response.headers.get(
              'Content-Disposition'
            );
            const filename =
              this.getFilenameFromContentDisposition(contentDisposition);
            const blob = new Blob([response.body]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
          } else {
            console.error('Response body is empty or invalid.');
          }
        },
        error: (error) => {
          console.error('Error downloading file:', error);
        },
      });
  }

  private getFilenameFromContentDisposition(
    contentDisposition: string | null
  ): string {
    if (!contentDisposition) return 'downloaded-file';

    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
      contentDisposition
    );
    return matches != null && matches[1]
      ? matches[1].replace(/['"]/g, '')
      : 'downloaded-file';
  }

  clearQASelection(): void {
    this.selectedQACodes = '';
    this.itemForm.get('itemTempDto.itemQa')?.setValue('');
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = undefined;
    }

    this.getUomSubscription?.unsubscribe();
    this.getPrioritySubscription?.unsubscribe();
    this.updateItemSubscription?.unsubscribe();
    this.saveItemSubscription?.unsubscribe();
    this.deleteAttachmentSubscription?.unsubscribe();
    this.getItemDetailsSubscription?.unsubscribe();
    this.getAttachmentsSubscription?.unsubscribe();
    this.downloadFileSubscription?.unsubscribe();
  }
}
